// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { StateSpecificMetricCopy } from "../types";

const content: StateSpecificMetricCopy = {
  prisonPopulationOverTime: {
    title: "Prison population over time",
    note: "Data labeled May 1, 2024 was actually generated May 4, 2024.",
    methodology:
      'The chart describes the historical incarceration population over the selected "Time Period". Each data point represents the total population for the selected group on the first day of that month. For example, hovering over the "November 2020" data point on the chart will show the total number of people described in the section above on November 1, 2020.',
  },
  prisonPopulationBySex: {
    title: "Prison population by sex",
    methodology:
      "The chart describes the number of people by sex as of the date specified in the chart title.",
  },
  prisonPopulationByGender: {
    title: "Prison population by gender identity",
    methodology:
      "The chart describes the number of people from each gender identity as of the date specified in the chart title. ",
  },
  prisonPopulationByEthnicity: {
    title: "Prison population by ethnic status",
    methodology:
      "The chart describes the number of people by self-reported ethnicity as of the date specified in the chart title.",
  },
  prisonPopulationBySentenceLengthMin: {
    title: "Prison population by minimum sentence (months)",
    methodology:
      "The chart describes the number of people within each minimum sentence category (in months) as of the date specified in the chart title. Sentence length represents aggregate sentence, taking into account the sentences for all crimes of conviction.",
  },
  prisonPopulationBySentenceLengthMax: {
    title: "Prison population by maximum sentence (months)",
    methodology:
      "The chart describes the number of people within each maximum sentence category (in months) as of the date specified in the chart title. Sentence length represents aggregate sentence, taking into account the sentences for all crimes of conviction.",
  },
  prisonPopulationByChargeCountyCode: {
    title: "Prison population by conviction county",
    methodology:
      "The chart describes the number of people from each county of conviction as of the date specified in the chart title.",
  },
  prisonPopulationByOffenseType: {
    title: "Prison population by crime group",
    methodology:
      "The chart describes the number of people within each crime group category as of the date specified in the chart title.",
  },
  prisonPopulationByRace: {
    title: "Prison population by race",
    methodology:
      "The chart describes the number of people by self-reported race as of the date specified in the chart title.",
  },
  prisonPopulationByAgeGroup: {
    title: "Prison population by age group",
    methodology:
      "The chart describes the number of people in each age group as of the date specified in the chart title.",
  },
  prisonPopulationByAdmissionReason: {
    title: "Prison population by latest admission type",
    methodology:
      "The chart describes the number of people by latest admission type as of the date specified in the chart title.",
    subpoints:
      "* JS Admission = Judicially Sanctioned by the court to participate in a drug treatment program at DOCCS.  These individuals are admitted to DOCCS facilities and are also under parole supervision. \n* PDP Admission = Admitted to DOCCS to participate in a Parole Diversion Program as the result of a parole violation.  These individuals are admitted to DOCCS facilities and are also under parole supervision.  This program was discontinued in 2022.\n* No New Term PV = Returned to DOCCS custody for violating the conditions of parole in an important respect.\n* New Term PV = Admitted to DOCCS for a new conviction that occurred while they were on parole supervision.",
  },
  prisonPopulationByChargeDescription: {
    title: "Prison population by specific crime",
    methodology:
      "The chart describes the number of people by specific crime as of the date specified in the chart title.",
  },
  prisonPopulationByReligion: {
    title: "Prison population by religion",
    methodology:
      "The chart describes the number of people from each religion as of the date specified in the chart title.",
    subpoints:
      "* The vast majority (~95%) of individuals labeled with Not Specified/Unknown on the Religion dashboard fall into the Not Specified category, meaning they did not provide a religious affiliation when asked. The remaining individuals, who fall into the Unknown category, are nearly all newly admitted to the Department and have not yet had all of their information entered on the computer system.",
  },
  prisonPopulationByMaritalStatus: {
    title: "Prison population by marital status",
    methodology:
      "The chart describes the number of people from each marital status as of the date specified in the chart title.",
  },
  prisonPopulationByTimeAtFacility: {
    title: "Prison population by time at housing facility",
    methodology:
      "The chart describes the number of people from each time at each housing facility as of the date specified in the chart title.",
  },
  admissionsAndReleasesOverTime: {
    title: "Admissions and releases over time",
    titleForAdmissions: "Admissions over time",
    titleForReleases: "Releases over time",
    methodology:
      'The chart describes the historical admissions and releases from prison over the selected "Time Period". Each data point represents the total admissions and total releases for the selected group on the first day of that month. For example, hovering over the "November 2023" data point on the chart will show the total number of people described in the section above on November 1, 2023.',
  },
  releasesByCommunitySupervision: {
    title: "Releases by community supervision over time",
    methodology:
      "The chart describes the number of people by community supervision status specified in the chart title.",
  },
  releasesByType: {
    title: "Releases by type",
    methodology:
      "The chart describes the number of people by release type specified in the chart title.",
  },
  admissionsByType: {
    title: "Admissions by type",
    methodology:
      "The chart describes the number of people by admission type specified in the chart title.",
  },
  admissionsAndReleasesByCustodyStatus: {
    title: "Admissions and releases by custody status",
    titleForAdmissions: "Admissions by custody status",
    titleForReleases: "Releases by custody status",
    methodology:
      "The chart describes the number of historical admissions and releases of people by custody status specified in the chart title.",
  },
};

export default content;
